import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Download, User, Phone, Hash, Building, RotateCw, ArrowLeft, Edit, Users, Filter, RotateCcw } from 'lucide-react';
import { useGetProfile } from '../hooks/useProfile.ts';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/axios.ts';

const IDCardGenerator = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { data: profile, isLoading: isProfileLoading } = useGetProfile();
  
  // Fetch all members for admin
  const { data: members, isLoading: isMembersLoading } = useQuery({
    queryKey: ['members'],
    queryFn: async () => {
      const { data } = await api.get('/members');
      return data;
    },
    enabled: !memberId // Only fetch when not editing a specific member
  });

  // Fetch specific member data when editing
  const { data: memberData, isLoading: isMemberLoading } = useQuery({
    queryKey: ['member', memberId],
    queryFn: async () => {
      const { data } = await api.get(`/members/${memberId}`);
      return data;
    },
    enabled: !!memberId
  });
  
  const [formData, setFormData] = useState({
    name: '',
    id: '',
    phone: '',
    groupName: '',
    position: '',
    profileImage: null
  });
  
  const [profilePreview, setProfilePreview] = useState(null);
  const [rotation, setRotation] = useState(0); // For edit page
  const [memberRotations, setMemberRotations] = useState({}); // For member list
  const [isRotating, setIsRotating] = useState(false);
  const [photoPosition, setPhotoPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Filter state - load from localStorage on mount
  const [filterFromId, setFilterFromId] = useState(() => {
    return localStorage.getItem('id_filter_from') || '';
  });
  const [filterToId, setFilterToId] = useState(() => {
    return localStorage.getItem('id_filter_to') || '';
  });
  
  const cardRef = useRef(null);
  const fileInputRef = useRef(null);
  const photoRef = useRef(null);

  // Auto-populate form with user data
  useEffect(() => {
    if (memberId && memberData?.data) {
      // Editing specific member
      const member = memberData.data;
      setFormData(prev => ({
        ...prev,
        name: member.name || '',
        phone: member.phone_number || '',
        groupName: member.current_group?.name || profile?.data?.current_group?.name || '',
        position: member.current_role || 'Member',
        id: member.id?.toString() || ''
      }));
    } else if (profile?.data && !memberId) {
      // Current user's data
      const userData = profile.data;
      setFormData(prev => ({
        ...prev,
        name: userData.name || '',
        phone: userData.phone_number || '',
        groupName: userData.current_group?.name || '',
        position: userData.current_role || 'Member',
        id: userData.id?.toString() || ''
      }));
    }
  }, [profile, memberData, memberId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target.result;
        setProfilePreview(imageData);
        setFormData(prev => ({
          ...prev,
          profileImage: imageData
        }));
        
        // Store image uniquely per member ID
        const currentMemberId = memberId || profile?.data?.id;
        if (currentMemberId) {
          localStorage.setItem(`member_image_${currentMemberId}`, imageData);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Rotation animation - flip to back side (for edit page)
  const handleRotate = () => {
    setIsRotating(true);
    setRotation(prev => prev + 180); // Flip 180 degrees to show back
    setTimeout(() => setIsRotating(false), 1000);
  };

  // Handle rotation for specific member in list
  const handleMemberRotate = (memberId) => {
    const memberRotation = memberRotations[memberId] || 0;
    setMemberRotations(prev => ({
      ...prev,
      [memberId]: memberRotation + 180
    }));
  };

  // Dragging functionality
  const handleMouseDown = (e) => {
    if (!photoRef.current) return;
    
    setIsDragging(true);
    const rect = photoRef.current.getBoundingClientRect();
    setDragStart({
      x: e.clientX - rect.left - photoPosition.x,
      y: e.clientY - rect.top - photoPosition.y
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !photoRef.current) return;
    
    const rect = photoRef.current.getBoundingClientRect();
    const newX = e.clientX - rect.left - dragStart.x;
    const newY = e.clientY - rect.top - dragStart.y;
    
    setPhotoPosition({ x: newX, y: newY });
  }, [isDragging, dragStart]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart, handleMouseMove]);

  // Filter members by ID range
  const filteredMembers = members?.data?.filter((member) => {
    const memberId = parseInt(member.id);
    const fromId = filterFromId ? parseInt(filterFromId) : 0;
    const toId = filterToId ? parseInt(filterToId) : Infinity;
    
    return memberId >= fromId && memberId <= toId;
  }) || [];

  // Save filters to localStorage
  const saveFilters = (fromId, toId) => {
    localStorage.setItem('id_filter_from', fromId);
    localStorage.setItem('id_filter_to', toId);
  };

  // Handle filter changes
  const handleFilterFromChange = (value) => {
    setFilterFromId(value);
    saveFilters(value, filterToId);
  };

  const handleFilterToChange = (value) => {
    setFilterToId(value);
    saveFilters(filterFromId, value);
  };

  // Reset filter function
  const resetFilter = () => {
    setFilterFromId('');
    setFilterToId('');
    saveFilters('', '');
  };

  // Get min and max member IDs for input constraints
  const memberIds = members?.data?.map(member => parseInt(member.id)) || [];
  const minMemberId = memberIds.length > 0 ? Math.min(...memberIds) : 1;
  const maxMemberId = memberIds.length > 0 ? Math.max(...memberIds) : 1;

  // Member list view
  if (!memberId && !isMembersLoading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="container px-6 py-8 mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-800">
              Group Member Card Generator{" "}
              <span className="ml-0 text-lg font-semibold align-top bg-clip-text bg-gradient-to-r from-purple-500 via-green-500 to-blue-500">
                <span className='text-transparent'>New!</span>🥳
              </span>
            </h1>
          </div>

          <div className="p-6 bg-white rounded-lg shadow">
            {/* Filter Section */}
            <div className="p-4 mb-6 bg-gray-50 rounded-lg">
              <div className="flex items-center mb-4">
                <Filter className="mr-2 w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-700">Filter Members by ID Range</h3>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-600">From ID:</label>
                  <input
                    type="number"
                    value={filterFromId}
                    onChange={(e) => handleFilterFromChange(e.target.value)}
                    placeholder={`e.g., ${minMemberId}`}
                    min={minMemberId}
                    max={maxMemberId}
                    className="px-3 py-2 w-24 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-600">To ID:</label>
                  <input
                    type="number"
                    value={filterToId}
                    onChange={(e) => handleFilterToChange(e.target.value)}
                    placeholder={`e.g., ${maxMemberId}`}
                    min={minMemberId}
                    max={maxMemberId}
                    className="px-3 py-2 w-24 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <button
                  onClick={resetFilter}
                  className="flex items-center px-3 py-2 text-sm text-gray-600 bg-white rounded-md border border-gray-300 transition-colors hover:bg-gray-50"
                >
                  <RotateCcw className="mr-1 w-4 h-4" />
                  Reset
                </button>
                <div className="text-sm text-gray-500">
                  Showing {filteredMembers.length} of {members?.data?.length || 0} members
                  <span className="ml-2 text-xs text-gray-400">
                    (Available IDs: {minMemberId} - {maxMemberId})
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center mb-6">
              <h2 className="flex items-center mb-6 text-xl font-semibold">
                <Users className="mr-2 w-5 h-5" />
                Member Cards ({filteredMembers.length} members)
              </h2>
              <button
                onClick={() => navigate('/download')}
                className="flex items-center px-4 py-2 text-white bg-purple-600 rounded-md transition-colors hover:bg-purple-700"
              >
                <Download className="mr-2 w-4 h-4" />
                Download All Cards
              </button>
            </div>

            {isMembersLoading ? (
              <div className="py-8 text-center">
                <div className="mx-auto w-8 h-8 rounded-full border-b-2 border-green-600 animate-spin"></div>
                <p className="mt-2 text-gray-600">Loading members...</p>
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="py-8 text-center">
                <div className="text-gray-500">
                  <Filter className="mx-auto mb-4 w-12 h-12 text-gray-400" />
                  <p className="text-lg font-medium">No members found</p>
                  <p className="text-sm">Try adjusting your ID range filter</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                {filteredMembers.map((member) => (
                  <div 
                    key={member.id}
                    className="p-4 w-full bg-white rounded-lg shadow"
                  >


                    {/* ID Card Preview with Flip Animation */}
                    <div className="flex justify-center">
                      <div
                        className="relative p-4 rounded-lg shadow-xl transition-transform duration-1000 ease-in-out"
                        style={{
                          width: '300px',
                          height: '189px',
                          transform: `rotateY(${(memberRotations[member.id] || 0)}deg)`,
                          transformStyle: 'preserve-3d',
                          perspective: '1000px'
                        }}
                      >
                        {/* Front Side */}
                        <div
                          className="flex overflow-hidden absolute inset-0 justify-center items-center bg-white rounded-lg"
                          style={{
                            backfaceVisibility: 'hidden',
                            zIndex: 2
                          }}
                        >
                          <img
                            src="/ID/4.png"
                            alt="ID Card Front"
                            className="w-full max-h-full"
                          />
                          <h4 className="absolute capitalize top-[40.3%] left-center w-[41%] overflow-hidden mr-[8%] text-base font-bold text-gray-800 -translate-x-1/2 -translate-y-1/2">
                            {member.name || 'Unknown'}
                          </h4>
                          <span className='absolute capitalize text-xs top-[45.3%] left-center w-[41%] overflow-hidden mr-[8%]'>
                            <span className="font-medium"></span> {member.current_group?.name || profile?.data?.current_group?.name || 'General'}
                          </span>
                          <span className='absolute text-xs top-[52.2%] left-center w-[41%] overflow-hidden mr-[8%]'>
                            <span className="font-medium"></span> {member.id}
                          </span>
                          <span className='absolute text-xs top-[58.9%] left-center w-[41%] overflow-hidden mr-[8%]'>
                            <span className="font-medium"></span> {member.phone_number || 'N/A'}
                          </span>
                          <div className="absolute flex-shrink-0 top-[30.9%] right-center w-[41%] overflow-hidden ml-[57.5%]">
                            <div className="relative cursor-move">
                              {(() => {
                                // Check for stored image first, then fallback to member.profile_image
                                const storedImage = localStorage.getItem(`member_image_${member.id}`);
                                const imageToShow = storedImage || member.profile_image;
                                
                                return imageToShow ? (
                                  <img
                                    src={imageToShow}
                                    alt="Profile"
                                    className="object-cover w-[102px] h-[89px] rounded-full border-4 border-indigo-100 select-none"
                                    draggable={true}
                                  />
                                ) : (
                                  <div className="flex justify-center items-center w-[99px] h-[89px] bg-gray-200 rounded-full border-4 border-indigo-100">
                                    <User className="w-9 h-9 text-gray-400" />
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>

                        {/* Back Side */}
                        <div
                          className="flex overflow-hidden absolute inset-0 justify-center items-center bg-white rounded-lg"
                          style={{
                            backfaceVisibility: 'hidden',
                            transform: 'rotateY(180deg)',
                            zIndex: 1
                          }}
                        >
                          <img
                            src="/ID/3.png"
                            alt="ID Card Back"
                            className="w-full max-h-full"
                          />
                        </div>
                      </div>
                        </div>
                        
                                            <div className="flex relative items-center mt-8 mb-4">
                      <div className="flex absolute right-6 space-x-2">
                        <button
                          onClick={() => handleMemberRotate(member.id)}
                          className="flex items-center px-3 py-2 text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700 disabled:opacity-50"
                          title="Flip ID Card"
                        >
                          <RotateCw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/id/${member.id}`)}
                          className="flex items-center px-4 py-2 text-white bg-green-600 rounded-md transition-colors hover:bg-green-700"
                        >
                          <Edit className="mr-2 w-4 h-4" />
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isProfileLoading || isMemberLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full border-b-2 border-green-600 animate-spin"></div>
          <p className="mt-4 text-gray-600">Loading profile data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container px-6 py-8 mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            {memberId && (
              <button
                onClick={() => navigate('/id')}
                className="flex items-center px-3 py-2 mr-4 text-gray-600 transition-colors hover:text-gray-800"
              >
                <ArrowLeft className="mr-2 w-4 h-4" />
                Back to List
              </button>
            )}
            <h1 className="text-3xl font-bold text-gray-800">
              {memberId ? 'Edit Member Card' : 'Group Member Card Generator'}{" "}
              {!memberId && (
                <span className="ml-0 text-lg font-semibold align-top bg-clip-text bg-gradient-to-r from-purple-500 via-green-500 to-blue-500">
                  <span className='text-transparent'>New!</span>🥳
                </span>
              )}
        </h1>
          </div>
        </div>
        
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Form Section */}
          <div className="p-6 w-full bg-white rounded-lg shadow lg:w-1/2">
            <h2 className="mb-6 text-xl font-semibold">
              {memberId ? 'Edit Member Information' : 'Member Information'}
            </h2>
            
            <form>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                  Full Name
                </label>
                  <div className="relative">
                    <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                      <User size={18} className="text-gray-400" />
                    </div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                      className="block py-2 pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50"
                  placeholder="Enter full name"
                />
                  </div>
              </div>

              <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                  Employee ID
                </label>
                  <div className="relative">
                    <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                      <Hash size={18} className="text-gray-400" />
                    </div>
                <input
                  type="text"
                  name="id"
                  value={formData.id}
                  onChange={handleInputChange}
                      className="block py-2 pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50"
                  placeholder="Enter employee ID"
                />
                  </div>
              </div>

              <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                  <div className="relative">
                    <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                      <Phone size={18} className="text-gray-400" />
                    </div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                      className="block py-2 pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50"
                  placeholder="Enter phone number"
                />
              </div>
              </div>

              <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Group Name
                </label>
                  <div className="relative">
                    <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                      <Building size={18} className="text-gray-400" />
                    </div>
                <input
                  type="text"
                      name="groupName"
                      value={formData.groupName}
                  onChange={handleInputChange}
                      className="block py-2 pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50"
                      placeholder="Enter group name"
                />
                  </div>
              </div>

                <div className="md:col-span-2">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                  Position/Title
                </label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                    className="block px-3 py-2 w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50"
                  placeholder="Enter position"
                />
              </div>

                <div className="md:col-span-2">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                  Profile Photo
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  accept="image/*"
                    className="block px-3 py-2 w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                />
              </div>
            </div>
            </form>
          </div>

          {/* Preview Section */}
          <div className="p-6 w-full bg-white rounded-lg shadow lg:w-1/2">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-700">Member Card Preview</h2>
              <div className="flex space-x-2">
                <button
                  onClick={handleRotate}
                  disabled={isRotating}
                  className="flex items-center px-3 py-2 text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700 disabled:opacity-50"
                  title="Flip ID Card"
                >
                  <RotateCw className={`w-4 h-4 ${isRotating ? 'animate-spin' : ''}`} />
                </button>
                {/* Only show download button on main list page, not on edit page */}
                {!memberId && (
                  <button
                    onClick={() => navigate('/download')}
                    className="flex items-center px-4 py-2 text-white bg-green-600 rounded-md transition-colors hover:bg-green-700"
                  >
                    <Download className="mr-2 w-4 h-4" />
                    Download
                  </button>
                )}
              </div>
            </div>

            {/* ID Card Preview with Flip Animation */}
            <div className="flex justify-center">
            <div 
              ref={cardRef}
                className="relative p-4 rounded-lg shadow-xl transition-transform duration-1000 ease-in-out"
                style={{
                  width: '400px',
                  height: '252px',
                  transform: `rotateY(${rotation}deg)`,
                  transformStyle: 'preserve-3d',
                  perspective: '1000px'
                }}
            >
                {/* Front Side */}
                <div
                  className="flex overflow-hidden absolute inset-0 justify-center items-center bg-white rounded-lg"
                  style={{
                    backfaceVisibility: 'hidden',
                    zIndex: 2
                  }}
                >
                  <img
                    src="/ID/4.png"
                    alt="ID Card Front"
                    className="w-full max-h-full"
                  />
                  <h4 className="absolute capitalize top-[40.3%] left-center w-[41%] overflow-hidden mr-[8%] text-base font-bold text-gray-800 -translate-x-1/2 -translate-y-1/2">
                    {formData.name || 'John Doe'}
                  </h4>
                  <span className='absolute capitalize text-xs top-[45.3%] left-center w-[41%] overflow-hidden mr-[8%]'>
                    <span className="font-medium"></span> {formData.groupName || 'General'}
                  </span>
                  <span className='absolute text-xs top-[52.2%] left-center w-[41%] overflow-hidden mr-[8%]'>
                    <span className="font-medium"></span> {formData.id || 'EMP001'}
                  </span>
                  <span className='absolute text-xs top-[58.9%] left-center w-[41%] overflow-hidden mr-[8%]'>
                    <span className="font-medium"></span> {formData.phone || '+1234567890'}
                  </span>
                  <div className="absolute flex-shrink-0 top-[30.9%] right-center w-[41%] overflow-hidden ml-[57.5%]">
                    <div
                      className="relative cursor-move"
                      onMouseDown={handleMouseDown}
                    >
                    {(() => {
                      // Check for stored image first, then profilePreview, then member.profile_image
                      const currentMemberId = memberId || profile?.data?.id;
                      const storedImage = currentMemberId ? localStorage.getItem(`member_image_${currentMemberId}`) : null;
                      const imageToShow = profilePreview || storedImage || (memberData?.data?.profile_image);
                      
                      return imageToShow ? (
                        <img
                          src={imageToShow}
                          alt="Profile"
                          className="object-cover w-[136px] h-[119px] rounded-full border-4 border-indigo-100 select-none"
                          draggable={true}
                        />
                      ) : (
                        <div className="flex justify-center items-center w-[132px] h-[119px] bg-gray-200 rounded-full border-4 border-indigo-100">
                          <User className="w-12 h-12 text-gray-400" />
                        </div>
                      );
                    })()}
                    </div>
                  </div>
                </div>

                {/* Back Side */}
                <div
                  className="flex overflow-hidden absolute inset-0 justify-center items-center bg-white rounded-lg"
                  style={{
                    backfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)',
                    zIndex: 1
                  }}
                >
                  <img
                    src="/ID/3.png"
                    alt="ID Card Back"
                    className="w-full max-h-full"
                  />
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-2 text-sm text-center text-gray-500">
              <p>Fill out the form to see changes reflected in the preview.</p>
              <p>💡 <strong>Tips:</strong> Click the flip button to see the back of the ID card. Drag the photo to reposition it perfectly!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IDCardGenerator;